import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CREDIT_PACKS,
  creditPacks,
  type CreditPack,
  type Payment,
} from "@zworks/console-contracts";
import * as React from "react";
import { AlertTriangle, Check, CreditCard, Loader2, Lock, X } from "lucide-react";
import { EmptyState, ErrorState, LoadingState, NotConnectedState } from "@/components/states";
import { PageHeader } from "@/components/bespoke";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ApiError, billingQuery, createCheckout } from "@/lib/api";
import {
  checkoutOrder,
  paymentStateBadges,
  taxLabel,
  VAT_RATE_PERCENT,
  type PaymentState,
} from "@/lib/checkout";
import { formatCentsUsd, formatCredits } from "@/lib/format";
import { useCurrentProject } from "@/lib/project";

export function PaymentStateBadge({ state }: { state: PaymentState }) {
  const badge = paymentStateBadges[state];
  return <Badge variant={badge.variant}>{badge.label}</Badge>;
}

function CheckoutHeading({ description }: { description?: string }) {
  return <PageHeader title="Buy credits" description={description} />;
}

const packNames: Record<CreditPack, string> = { starter: "Starter", team: "Team", scale: "Scale" };
const packSavings: Partial<Record<CreditPack, string>> = { team: "Save 5%", scale: "Save 10%" };

function PackPicker({
  value,
  onChange,
}: {
  value: CreditPack;
  onChange: (pack: CreditPack) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-3 border-0 p-0">
      <legend className="text-sm font-medium">Choose a credit tier</legend>
      <div className="grid gap-3 sm:grid-cols-3">
        {creditPacks.map((pack) => {
          const priced = CREDIT_PACKS[pack];
          const selected = value === pack;
          return (
            <label
              key={pack}
              className={`flex cursor-pointer flex-col gap-1 rounded-lg border p-4 ${selected ? "border-primary ring-1 ring-primary" : "border-border"}`}
            >
              <input
                className="sr-only"
                type="radio"
                name="credit-tier"
                checked={selected}
                onChange={() => onChange(pack)}
              />
              <span className="flex items-center justify-between text-xs text-muted-foreground">
                {packNames[pack]}
                {packSavings[pack] ? <Badge variant="success">{packSavings[pack]}</Badge> : null}
              </span>
              <b className="text-lg tabular-nums">{formatCredits(priced.credits)}</b>
              <span className="text-sm tabular-nums">{formatCentsUsd(priced.priceCents)}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function OrderSummary({
  pack,
  balance,
  taxRate,
}: {
  pack: CreditPack;
  balance: number | null;
  taxRate: number | null | undefined;
}) {
  const rate = taxRate === undefined ? VAT_RATE_PERCENT / 100 : taxRate;
  const order = checkoutOrder(pack, rate);
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm font-medium">Order summary</p>
      <dl className="flex flex-col gap-2 text-sm">
        <div className="flex justify-between">
          <dt className="text-muted-foreground">{formatCredits(order.credits)} credits</dt>
          <dd className="tabular-nums">{formatCentsUsd(order.priceCents)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">{taxLabel(rate)}</dt>
          <dd className="tabular-nums">{formatCentsUsd(order.vatCents)}</dd>
        </div>
        <div className="my-1 border-t" />
        <div className="flex justify-between text-base">
          <dt className="font-semibold">Total today</dt>
          <dd className="font-semibold tabular-nums">{formatCentsUsd(order.totalCents)}</dd>
        </div>
      </dl>
      {balance !== null ? (
        <div className="flex flex-col gap-1 text-sm text-muted-foreground">
          <div className="flex justify-between">
            <span>Balance now</span>
            <span className="tabular-nums">{formatCredits(balance)}</span>
          </div>
          <div className="flex justify-between">
            <span>After payment</span>
            <b className="text-foreground tabular-nums">{formatCredits(balance + order.credits)}</b>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** The state after paying, as the CheckoutStates board draws it. Never shown as done until confirmed. */
export function PaymentOutcome({ payment }: { payment: Payment }) {
  if (payment.status === "pending") {
    return (
      <div className="flex flex-col items-center gap-3 py-4 text-center">
        <span className="flex size-10 items-center justify-center rounded-full border">
          <Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden />
        </span>
        <h2 className="text-base font-semibold">Payment submitted</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          We have sent your payment of{" "}
          <b className="text-foreground tabular-nums">{formatCentsUsd(payment.amountCents)}</b> to
          your bank. Your credits are not added yet. This usually takes under a minute.
        </p>
        <PaymentStateBadge state="pending" />
        <p className="text-xs text-muted-foreground">
          You can close this page. The ledger shows the purchase as Pending, and updates by itself.
        </p>
      </div>
    );
  }
  if (payment.status === "needs-authentication") {
    return (
      <div className="flex flex-col items-center gap-3 py-4 text-center">
        <span className="flex size-10 items-center justify-center rounded-full bg-warning/12 text-warning">
          <Lock className="size-5" aria-hidden />
        </span>
        <h2 className="text-base font-semibold">Your bank needs to confirm</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          Your bank asks you to approve this payment. A secure window opens from your bank. Nothing
          has been charged yet.
        </p>
        <PaymentStateBadge state="needs-authentication" />
      </div>
    );
  }
  if (payment.status === "confirmed") {
    return (
      <div className="flex flex-col items-center gap-3 py-4 text-center">
        <span className="flex size-10 items-center justify-center rounded-full bg-success/12 text-success">
          <Check className="size-5" aria-hidden />
        </span>
        <h2 className="text-base font-semibold">Payment confirmed</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          <b className="text-foreground tabular-nums">{formatCredits(payment.credits)} credits</b>{" "}
          were added to the project.
        </p>
        <PaymentStateBadge state="confirmed" />
      </div>
    );
  }
  if (payment.status === "declined") {
    return (
      <div className="flex flex-col items-center gap-3 py-4 text-center">
        <span className="flex size-10 items-center justify-center rounded-full bg-danger/12 text-danger">
          <X className="size-5" aria-hidden />
        </span>
        <h2 className="text-base font-semibold">Your card was declined</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          Your bank did not accept the payment.{" "}
          <b className="text-foreground">You have not been charged</b> and no credits were added.
          Try another card or contact your bank.
        </p>
        <PaymentStateBadge state="declined" />
      </div>
    );
  }
  if (payment.status === "refunded") {
    return (
      <div className="flex flex-col items-center gap-3 py-4 text-center">
        <span className="flex size-10 items-center justify-center rounded-full border">
          <CreditCard className="size-5 text-muted-foreground" aria-hidden />
        </span>
        <h2 className="text-base font-semibold">This payment was refunded</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          <b className="text-foreground tabular-nums">{formatCredits(payment.credits)} credits</b>{" "}
          were returned to the project balance. Your statement shows the reversal within a few days.
        </p>
        <PaymentStateBadge state="refunded" />
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center">
      <span className="flex size-10 items-center justify-center rounded-full bg-unknown/12 text-unknown">
        <AlertTriangle className="size-5" aria-hidden />
      </span>
      <h2 className="text-base font-semibold">We could not confirm the payment</h2>
      <p className="max-w-md text-sm text-muted-foreground">
        We have not heard back from the payment provider.{" "}
        <b className="text-foreground">Please do not pay again.</b> We keep checking and will update
        this page. Your card may show a pending charge.
      </p>
      <PaymentStateBadge state="unknown" />
      <span className="font-mono text-xs text-muted-foreground">{payment.id}</span>
    </div>
  );
}

/**
 * The screen a non-owner sees, as the CheckoutStates board draws it. It explains who can buy
 * and never offers a way to pay.
 */
export function MemberCheckoutView({ ownerName }: { ownerName?: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center">
      <span className="flex size-10 items-center justify-center rounded-full border">
        <Lock className="size-5 text-muted-foreground" aria-hidden />
      </span>
      <h2 className="text-base font-semibold">Only the owner can buy credits</h2>
      <p className="max-w-md text-sm text-muted-foreground">
        {ownerName
          ? `Credits are bought by the project owner, ${ownerName}. You can see the balance and ask them to top up.`
          : "Credits are bought by the project owner. You can see the balance and ask them to top up."}
      </p>
      <PaymentStateBadge state="member-view" />
    </div>
  );
}

/**
 * The checkout page, as the Checkout board draws it: the credit tier picker with the saved card as
 * brand and last four, the order summary with VAT, and the payment outcome afterwards. Only an
 * owner can buy; a member sees who can.
 */
export function CheckoutPage() {
  const { project, loaded } = useCurrentProject();
  const [pack, setPack] = React.useState<CreditPack>("team");
  const [payment, setPayment] = React.useState<Payment | null>(null);
  const checkout = useMutation({
    mutationFn: (next: CreditPack) => createCheckout(project?.id ?? "", { pack: next }),
    onSuccess: (created) => setPayment(created),
  });
  const billing = useQuery({
    ...billingQuery(project?.id ?? ""),
    enabled: project !== null && project.role === "owner",
  });

  if (!loaded) {
    return (
      <>
        <CheckoutHeading />
        <LoadingState label="Loading checkout" rows={6} />
      </>
    );
  }

  if (project === null) {
    return (
      <>
        <CheckoutHeading />
        <EmptyState
          title="No projects yet"
          description="Create your first project. Its checkout appears here."
        />
      </>
    );
  }

  if (project.role !== "owner") {
    return (
      <>
        <CheckoutHeading description={project.name} />
        <Card>
          <CardContent className="pt-6">
            <MemberCheckoutView />
          </CardContent>
        </Card>
      </>
    );
  }

  if (!payment && billing.isError) {
    const apiError = billing.error instanceof ApiError ? billing.error : null;
    return (
      <>
        <CheckoutHeading description={project.name} />
        <Card>
          <CardContent className="pt-6">
            <ErrorState
              title="Checkout could not be loaded"
              description={
                apiError?.problem?.detail ??
                "The project billing settings could not be read, so the VAT breakdown is unavailable."
              }
              requestId={apiError?.requestId ?? "unknown"}
              onRetry={() => void billing.refetch()}
            />
          </CardContent>
        </Card>
      </>
    );
  }

  const taxRate = billing.data?.taxRate ?? (billing.isPending ? undefined : null);
  const order = checkoutOrder(pack, taxRate);

  return (
    <>
      <CheckoutHeading description={project.name} />

      <Card>
        <CardContent className="flex flex-col gap-6 pt-6">
          {payment ? (
            <PaymentOutcome payment={payment} />
          ) : (
            <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
              <div className="flex flex-col gap-6">
                <PackPicker value={pack} onChange={setPack} />
                <NotConnectedState
                  title="Payment is not connected yet"
                  description="Checkout needs a payment provider before it can take a card. No card details are collected here until then."
                />
              </div>
              <div className="flex flex-col gap-4 rounded-lg bg-muted/45 p-5">
                {billing.isPending ? (
                  <LoadingState label="Loading order summary" rows={4} />
                ) : (
                  <>
                    <OrderSummary pack={pack} balance={project.creditBalance} taxRate={taxRate} />
                    <Button
                      size="lg"
                      className="w-full"
                      disabled={checkout.isPending}
                      onClick={() => checkout.mutate(pack)}
                    >
                      {checkout.isPending ? "Paying…" : `Pay ${formatCentsUsd(order.totalCents)}`}
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Credits are added when the payment is confirmed, usually within a minute. A
                      receipt is emailed to the billing contact.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {checkout.isError ? (
            <CheckoutError error={checkout.error} onRetry={() => checkout.mutate(pack)} />
          ) : null}
        </CardContent>
      </Card>
    </>
  );
}

function CheckoutError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  if (error instanceof ApiError && error.status === 403) {
    return <MemberCheckoutView />;
  }
  const apiError = error instanceof ApiError ? error : null;
  return (
    <ErrorState
      title="The payment could not be started"
      description={apiError?.problem?.detail ?? "The request could not be completed."}
      requestId={apiError?.requestId ?? "unknown"}
      onRetry={onRetry}
    />
  );
}
